// Phase 25 iter-17 (Lote B, M14) — BUYER-side EUDR DDS list (web).
//
// EU Reg. 2023/1115 obliga ao operador que coloca o produto no mercado a
// conservar os DDS dos provedores 5 anos. Este page expón a un comprador
// o listado dos DDS que xa lle teñen asociados, cun link ao PDF oficial.
// Reutiliza o endpoint `/api/v1/eudr/dds/received` xa exposto polo
// backend (mesma fonte que usa a app móbil).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ReceivedDdsItem } from '@leyrochain/shared';

import { listReceivedDds } from '../api/eudr';
import { describeAxiosError } from '../api/errors';
import InfoCallout from '../components/InfoCallout';
import Spinner from '../components/ui/Spinner';

function formatCents(cents: number | null): string {
  if (cents === null) return '—';
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('gl', { day: '2-digit', month: 'short', year: 'numeric' });
}

function RiskBadge({ level }: { level: ReceivedDdsItem['risk_level'] }): JSX.Element {
  const palette =
    level === 'LOW'
      ? 'bg-emerald-100 text-emerald-800'
      : level === 'STANDARD'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-red-100 text-red-800';
  const label =
    level === 'LOW' ? 'Risco baixo' : level === 'STANDARD' ? 'Risco estándar' : 'Risco alto';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${palette}`}>{label}</span>
  );
}

export default function EudrReceivedPage(): JSX.Element {
  const [items, setItems] = useState<ReceivedDdsItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listReceivedDds();
      setItems(res.items);
    } catch (e) {
      setError(describeAxiosError(e, 'Non se puido cargar a lista de DDS.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <main className="max-w-5xl mx-auto px-6 py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-extrabold text-dark">DDS recibidas (EUDR)</h1>
        <p className="mt-2 text-sm text-muted">
          Documentos de Dilixencia Debida das compras que fixeches. O Regulamento UE 2023/1115
          obriga a conservalos 5 anos.
        </p>
        <p className="mt-2 text-xs text-muted">
          <Link to="/marketplace/profile" className="text-rauxa underline">
            Volver ao perfil
          </Link>
        </p>
      </header>

      <div className="mb-6">
        <InfoCallout
          storageKey="eudr-list-explainer"
          defaultOpen={true}
          icon={<span aria-hidden>🌳</span>}
          title="Que son as Declaracións de Dilixencia Debida?"
          body={
            <>
              O Regulamento UE 2023/1115 obriga a demostrar que os animais procedentes do campo
              galego non veñen de zonas deforestadas. Cada compra xera unha DDS automaticamente.{' '}
              <strong>Garda estes documentos 5 anos</strong>: necesítaste se revendes a UE.
            </>
          }
        />
      </div>

      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => void reload()} className="ml-3 underline">
            Reintentar
          </button>
        </div>
      ) : items && items.length === 0 ? (
        <div className="rounded-md border border-border bg-surface p-6 text-sm text-muted text-center">
          Aínda non recibiches ningún DDS. Cando un vendedor xere un para unha venda túa, aparecerá
          aquí.
        </div>
      ) : (
        <ul className="space-y-4">
          {(items ?? []).map((item) => (
            <li
              key={item.id}
              className="rounded-lg border border-border bg-surface p-5 shadow-sm"
              data-testid={`dds-row-${item.id}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <span className="font-mono text-sm font-bold text-dark">
                  {item.reference_number}
                </span>
                <RiskBadge level={item.risk_level} />
              </div>
              <div className="text-sm text-dark">
                <span className="text-muted">Provedor:</span> {item.seller_name}
              </div>
              <div className="text-xs text-muted mt-1">
                {formatDate(item.sale_purchased_at)} · {formatCents(item.sale_amount_cents)} ·{' '}
                {item.animal_ids.length === 1 ? '1 animal' : `${item.animal_ids.length} animais`}
              </div>
              {item.pdf_url ? (
                <a
                  href={item.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-sm text-rauxa underline"
                >
                  Abrir PDF oficial ↗
                </a>
              ) : (
                <span className="mt-3 inline-block text-xs text-amber-700">
                  PDF non dispoñible aínda
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
